import { faMicrophoneSlash } from '@fortawesome/pro-solid-svg-icons';
import { TFunction } from 'i18next';
import { useMemo } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Icon, Text, cn } from '@growtherapy/sprout-ui';

import { getLocalizedPronoun } from '../utils';

function getScreenReaderLabel(
  t: TFunction,
  name: string | undefined,
  isSelf: boolean,
  pronounsText: string | null,
  audioMuted: boolean,
): string {
  const nameText = name ?? t('telehealth-core:participantInfo.participant');
  if (audioMuted) {
    if (isSelf) {
      return t('telehealth-core:participantInfo.youHaveYourMicrophoneMuted');
    }
    return pronounsText
      ? t('telehealth-core:participantInfo.namePronounsHasTheirMicrophoneMuted', {
          name: nameText,
          pronouns: pronounsText,
        })
      : t('telehealth-core:participantInfo.nameHasTheirMicrophoneMuted', {
          name: nameText,
        });
  }
  return pronounsText ? `${nameText} ${pronounsText}` : nameText;
}

export function NameBadge({
  name,
  className,
  isSelf,
  pronouns,
  audioMuted = false,
}: {
  name?: string;
  className?: string;
  isSelf: boolean;
  pronouns?: string[];
  audioMuted?: boolean;
}) {
  const t = useTranslate();

  const pronounsText = useMemo(() => {
    if (!pronouns?.length) {
      return null;
    }
    return `(${getLocalizedPronoun(t, pronouns[0])})`;
  }, [pronouns, t]);

  const screenReaderLabel = useMemo(
    () => getScreenReaderLabel(t, name, isSelf, pronounsText, audioMuted),
    [t, name, isSelf, pronounsText, audioMuted],
  );

  return (
    <div
      className={cn(
        'bg-lilac-700 rebrand:bg-surface-brand inline-flex justify-center items-center rounded-tr-2xl h-10 px-4 max-w-full box-border',
        className,
      )}
      data-testid='name-badge'
      role='note'
    >
      <span className='sr-only' data-testid='name-badge.sr-label'>
        {screenReaderLabel}
      </span>
      {audioMuted && (
        <Icon
          aria-hidden
          icon={faMicrophoneSlash}
          className={cn('text-neutral-100 rebrand:text-text-inverse', name && 'mr-2')}
          data-testid='audio-muted-badge'
        />
      )}
      {name && (
        <Text
          as='span'
          aria-hidden
          title={name}
          className='inline-block text-neutral-100 font-medium text-ellipsis overflow-hidden whitespace-nowrap fs-exclude rebrand:text-text-inverse'
          data-dd-privacy='mask'
          data-dd-action-name='Click on name badge'
          data-testid='name-badge.name'
        >
          {name}
          {pronounsText && (
            <span className='font-normal rebrand:text-text-inverse' data-testid='name-badge.pronouns'>
              {' '}
              {pronounsText}
            </span>
          )}
        </Text>
      )}
    </div>
  );
}
